/**
 * Контекст для управления авторизацией
 */

import {
  createContext, useContext, useEffect, useState, useCallback, useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@/types/api';
import {
  login as apiLogin,
  register as apiRegister,
  getMe,
  logout as apiLogout,
} from '@/api/authApi';
import { getToken } from '@/api/tokenStorage';
import { logger } from '@/utils/logger';

type AuthContextType = {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthProviderProps = {
  children: React.ReactNode;
};

/**
 * Провайдер контекста авторизации
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  /**
   * Проверка авторизации при загрузке
   */
  const checkAuth = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      const response = await getMe();
      if (response.success && response.user) {
        setUser(response.user);
      } else {
        // Токен невалидный, удаляем его
        apiLogout();
        setUser(null);
      }
    } catch (error) {
      logger.error('Ошибка проверки авторизации:', error);
      // Токен невалидный или истек, удаляем его
      apiLogout();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Вход пользователя
   */
  const handleLogin = useCallback(async (email: string, password: string) => {
    try {
      const response = await apiLogin({ email, password });
      if (response.success && response.user) {
        setUser(response.user);
        navigate('/admin');
      } else {
        throw new Error('Ошибка входа');
      }
    } catch (error) {
      logger.error('Ошибка входа:', error);
      throw error;
    }
  }, [navigate]);

  /**
   * Регистрация пользователя
   */
  const handleRegister = useCallback(async (email: string, password: string) => {
    try {
      const response = await apiRegister({ email, password });
      if (response.success && response.user) {
        setUser(response.user);
        navigate('/admin');
      } else {
        throw new Error('Ошибка регистрации');
      }
    } catch (error) {
      logger.error('Ошибка регистрации:', error);
      throw error;
    }
  }, [navigate]);

  /**
   * Выход пользователя
   */
  const handleLogout = useCallback(() => {
    apiLogout();
    setUser(null);
    navigate('/');
  }, [navigate]);

  // Проверяем авторизацию при монтировании
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value: AuthContextType = useMemo(() => ({
    user,
    loading,
    isAuthenticated: !!user,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    checkAuth,
  }), [user, loading, handleLogin, handleRegister, handleLogout, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Хук для использования контекста авторизации
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};

